import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useQueryClient } from 'react-query';
import { isEmpty } from 'validate.js';

import { get } from 'dpl/shared/utils/object';
import MiniOverlayModal from 'dpl/common/mini_overlay_modal/components/MiniOverlayModal';
import { sendAnalyticsEvent, sendAnalyticsIdentify } from 'dpl/util/analytics';
import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import QuizLayout from 'dpl/quiz/components/QuizLayout';
import UserAuthModalForgotPasswordForm from 'dpl/components/UserAuthModal/UserAuthModalForgotPasswordForm';
import UserAuthModalLoginForm from 'dpl/components/UserAuthModal/UserAuthModalLoginForm';
import { SIGNUP_FORM_TYPES } from 'dpl/shared/constants/shared';
import { QUERY_KEY_PREFIXES } from 'dpl/shared/fetching/utils/constants';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEWS,
  ANALYTICS_VIEW_FEATURES
} from 'dpl/constants/analytics';
import { MOBILE_LAYOUTS } from 'dpl/components/BreederProfile/utils/constants';
import { MOBILE_LAYOUT } from 'dpl/constants/query_params';
import { addToQuery } from 'dpl/util/queryString';

import { getSectionComponent, getQuestionComponent } from '../utils';
import { contactEditDefinition } from '../utils/queryDefinitions';
import {
  trackQuizQuestionSubmitted,
  trackQuizQuestionViewed
} from '../utils/analytics';
import useContactVerification from '../hooks/useContactVerification';
import { VERIFICATION_CODE_LENGTH } from '../utils/constants';
import ContactContext from '../utils/context';
import { SECTION_NAME as VERIFICATION_CODE_SECTION_NAME } from './sections/VerificationCodeSection';

export default function ContactQuiz() {
  const queryClient = useQueryClient();

  const isNewMobileLayout = useSelector(
    ({ queryParams }: any) => queryParams[MOBILE_LAYOUT] === MOBILE_LAYOUTS.NEW
  );

  const {
    breed_id: breedId = null,
    view,
    view_feature_location: viewFeatureLocation,
    origin_view_feature: originViewFeature
  } = useSelector(({ queryParams }: any) => queryParams);

  const {
    contactType,
    breederProfileId,
    breederProfilePath,
    dogId,
    dogUrl,
    litterId
  } = useSelector(({ server }: any) => server);

  // Buyer verification
  const { sendCode, checkCode, isFirstCodeSent, isCheckingCode } =
    useContactVerification();
  const [verificationRecipient, setVerificationRecipient] = useState<any>({});
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationError, setVerificationError] = useState('');

  const {
    value: { contact },
    isFetching
  } = useResourceQuery(
    contactEditDefinition(breederProfileId, contactType, dogId, breedId)
  );

  const { LOGIN, FORGOT_PASSWORD } = SIGNUP_FORM_TYPES;
  const [signupFormType, setSignupFormType] = useState<string | null>(null);

  const eventProps = {
    breeder_profile_id: breederProfileId,
    buyer_profile_id: contact?.buyer_profile_id,
    dog_id: dogId,
    litter_id: litterId,
    view: view ?? ANALYTICS_VIEWS.PUPPY_PROFILE,
    view_feature: ANALYTICS_VIEW_FEATURES.REQUEST_INFO_BUTTON,
    view_feature_location: viewFeatureLocation,
    origin_view_feature: originViewFeature
  };

  useEffect(() => {
    if (!isFetching && contact) {
      sendAnalyticsIdentify({ userId: contact.buyer_user_id });

      if (contact.new) {
        sendAnalyticsEvent(ANALYTICS_EVENTS.INITIAL_INQUIRY_STARTED_BY_BUYER, {
          quiz_name: contact.quiz_name,
          quiz_version: contact.quiz_version,
          ...eventProps
        });
      }

      get(contact, 'on_answered_events', []).forEach((event: any) => {
        sendAnalyticsEvent(event, {
          quiz_name: contact.quiz_name,
          quiz_version: contact.quiz_version,
          ...eventProps
        });
      });
    }
  }, [contact, isFetching]);

  function reloadQuiz() {
    queryClient.invalidateQueries(QUERY_KEY_PREFIXES.CONTACT_EDIT);
  }

  function isVerificationCodeSection(section: any) {
    return section.component_name === VERIFICATION_CODE_SECTION_NAME;
  }

  function handleNextClick(quiz: any, section: any, answers: any) {
    if (contact.is_incomplete_apple_user) {
      reloadQuiz();
    }
    // The code verification section tracks this analytics event separately
    if (!isVerificationCodeSection(section)) {
      trackQuizQuestionSubmitted(quiz, section, eventProps, answers);
    }
  }

  function handleSubmitClick(quiz: any, section: any) {
    trackQuizQuestionSubmitted(quiz, section, eventProps);
  }

  function handleQuestionViewed(quiz: any, section: any) {
    trackQuizQuestionViewed(quiz, section, eventProps);
  }

  function handleExitClick() {
    const redirectUrl = dogUrl || breederProfilePath;

    if (isNewMobileLayout) {
      const queryStr = addToQuery({ [MOBILE_LAYOUT]: MOBILE_LAYOUTS.NEW });
      window.location.href = `${redirectUrl}#${queryStr}`;
    } else {
      window.location.href = redirectUrl;
    }
  }

  function handleLoginClick() {
    setSignupFormType(LOGIN);
  }

  function handlePasswordResetClick() {
    setSignupFormType(FORGOT_PASSWORD);
  }

  function handleLoginClose() {
    setSignupFormType(null);
  }

  function handleAuth() {
    reloadQuiz();
    handleLoginClose();
  }

  function handleQuestionAnsweredEvent(quiz: any, eventName: any) {
    sendAnalyticsEvent(eventName, {
      quiz_name: quiz.name,
      quiz_version: quiz.version,
      ...eventProps
    });
  }

  function handleBeforeSubmitSection(_quiz: any, section: any) {
    if (!isVerificationCodeSection(section)) return null;

    // Prevent submitting verification section data. When the code
    // is verified, the quiz is reloaded and the verification question
    // is removed. When the code is incorrect, the boxes are cleared
    // and the verification question can be skipped by the user.
    return { cancel: true };
  }

  async function handleBeforeNextClick(quiz: any, section: any) {
    if (!isVerificationCodeSection(section)) {
      // Business as usual. Ignore verification section logic,
      // and allow advancing to the next section.
      return null;
    }

    if (
      !isEmpty(verificationCode) &&
      VERIFICATION_CODE_LENGTH > verificationCode.length
    ) {
      setVerificationError('Code must be 6 digits long');

      // Incomplete verification code entered.
      // Prevent advancing to the next section
      return { cancel: true };
    }

    if (!isEmpty(verificationRecipient) && !isEmpty(verificationCode)) {
      const validation = await checkCode({
        recipient: verificationRecipient,
        code: verificationCode
      });

      if (validation.success) {
        trackQuizQuestionSubmitted(quiz, section, {
          ...eventProps,
          quiz_question_response: 'verified'
        });

        // User has been verified. Reload quiz (so that verification
        // section is skipped on the server) and return early
        return reloadQuiz();
      }

      // Incorrect value entered. Clear verification code
      setVerificationCode('');

      // Prevent advancing to the next section
      return { cancel: true };
    }

    // Submit analytics tracking
    trackQuizQuestionSubmitted(quiz, section, {
      ...eventProps,
      quiz_question_response: 'skipped'
    });

    // Skip verification question and allow advancing
    // to the next section
    return null;
  }

  // Reset verification error when code changes
  useEffect(() => {
    setVerificationError('');
  }, [verificationCode]);

  if (isFetching) {
    return <div className="vh-100 loading-spinner" />;
  }

  return (
    <div className="ContactQuiz vh-100 bg-sand FullPageQuiz">
      <ContactContext.Provider
        value={{
          breederProfileId,
          dogId,
          handleLoginClick,
          verification: {
            sendCode,
            isFirstCodeSent,
            code: verificationCode,
            recipient: verificationRecipient,
            error: verificationError,
            setCode: setVerificationCode,
            setRecipient: setVerificationRecipient,
            eventProps
          }
        }}
      >
        {[LOGIN, FORGOT_PASSWORD].includes(signupFormType ?? '') && (
          <MiniOverlayModal
            className="UserAuthModal"
            onCloseClick={handleLoginClose}
          >
            {signupFormType === LOGIN ? (
              <UserAuthModalLoginForm
                loginOnly
                onPasswordResetClick={handlePasswordResetClick}
                onAuth={handleAuth}
              />
            ) : (
              <UserAuthModalForgotPasswordForm
                onLoginClick={handleLoginClick}
              />
            )}
          </MiniOverlayModal>
        )}
        <QuizLayout
          submissionId={contact.quiz_submission_id}
          getSectionComponent={getSectionComponent}
          getQuestionComponent={getQuestionComponent}
          onNextClick={handleNextClick}
          onSubmitClick={handleSubmitClick}
          onExitClick={handleExitClick}
          onNewSectionRendered={handleQuestionViewed}
          onAnsweredEvent={handleQuestionAnsweredEvent}
          headerCloseButtonLabel="Close"
          onBeforeNextClick={handleBeforeNextClick}
          onBeforeSubmitSection={handleBeforeSubmitSection}
          canAdvance={!verificationError && !isCheckingCode}
          showNavForMiddleInterstices
          v2MobileNav
        />
      </ContactContext.Provider>
    </div>
  );
}
